import Swal from "sweetalert2";
import store from "@/store";

async function uploadCostoCendis(
    axios: any,
    apiRoute: string,
    pedidoId: number,
    marca: string,
    file: File,
    importTemplateId: string | null,
    matchField: string | null,
    moneda: string | null,
    updateOdc: boolean,
    updateCendis: boolean,
    updateCantidad: boolean
) {
    const token = localStorage.getItem("authToken");
    const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
    };

    const formData = new FormData();
    formData.append("file", file);
    formData.append("id", String(pedidoId));
    formData.append("marca", marca);
    formData.append("update_odc", updateOdc ? "1" : "0");
    formData.append("update_cendis", updateCendis ? "1" : "0");
    formData.append("update_cantidad", updateCantidad ? "1" : "0");

    if (importTemplateId) {
        formData.append("import_template_id", importTemplateId);
    } else {
        formData.append("match_field", matchField ?? "codprod");
        formData.append("moneda", moneda ?? "BSS");
    }

    return axios.post(`${apiRoute}/pedido/costo-cendis/upload`, formData, { headers });
}

async function downloadCostoCendisTemplate(
    axios: any,
    apiRoute: string,
    matchField: string,
    updateOdc: boolean,
    updateCendis: boolean,
    updateCantidad: boolean
) {
    const token = localStorage.getItem("authToken");
    const response = await axios.get(`${apiRoute}/pedido/costo-cendis/template`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
            match_field: matchField,
            update_odc: updateOdc ? 1 : 0,
            update_cendis: updateCendis ? 1 : 0,
            update_cantidad: updateCantidad ? 1 : 0,
        },
        responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "plantilla_costo_cendis.xlsx");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

async function fetchImportTemplates(axios: any, apiRoute: string): Promise<any[]> {
    try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${apiRoute}/pedido/import-templates/available`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        return response.data?.data ?? [];
    } catch (error) {
        return [];
    }
}

export default async function openCostoCendisModal(
    groupedItems: any[],
    marca: string,
    axios: any,
    apiRoute: string,
    onSuccess?: () => void
) {
    const pedidoId = Array.isArray(groupedItems) && groupedItems.length > 0
        ? groupedItems[0]?.id
        : null;

    if (!pedidoId) {
        Swal.fire({
            icon: "error",
            title: "No se pudo determinar el pedido",
            text: "No hay items en este grupo de marca.",
        });
        return;
    }

    const importTemplates = await fetchImportTemplates(axios, apiRoute);

    let templateSelectHtml = "";
    if (importTemplates.length > 0) {
        templateSelectHtml = `
            <div class="mb-4">
                <label class="block text-sm font-semibold text-gray-700 mb-1.5">Plantilla de importación</label>
                <select id="cc_template" class="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring focus:ring-blue-200 focus:border-blue-500">
                    <option value="">Configurar manualmente</option>
                    ${importTemplates
                        .map(
                            (t: any) =>
                                `<option value="${t.id}" ${t.is_default ? "selected" : ""}>${t.name}</option>`
                        )
                        .join("")}
                </select>
            </div>
        `;
    }

    const html = `
        <div class="text-left text-sm">
            <div class="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 mb-4 text-xs text-blue-700 leading-relaxed">
                Solo actualiza el/los campo(s) que marques, en los productos que ya están en este pedido
                y coinciden con el código/barra del Excel. No agrega productos nuevos, ni modifica
                descuentos u otros campos.
            </div>

            <div class="mb-4">
                <label class="block text-sm font-semibold text-gray-700 mb-2">¿Qué actualizar?</label>
                <div class="flex gap-3">
                    <label id="cc_odc_label" class="flex-1 flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" id="cc_update_odc" class="rounded border-gray-300 text-blue-600" />
                        <span class="text-sm text-gray-700">Costo ODC</span>
                    </label>
                    <label id="cc_cendis_label" class="flex-1 flex items-center gap-2 border border-blue-500 bg-blue-50 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" id="cc_update_cendis" checked class="rounded border-gray-300 text-blue-600" />
                        <span class="text-sm text-gray-700">Costo Cendis</span>
                    </label>
                    <label id="cc_cantidad_label" class="flex-1 flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2.5 cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" id="cc_update_cantidad" class="rounded border-gray-300 text-blue-600" />
                        <span class="text-sm text-gray-700">Cantidad</span>
                    </label>
                </div>
            </div>

            ${templateSelectHtml}

            <div id="cc_manual_fields" class="${importTemplates.some((t: any) => t.is_default) ? "hidden" : ""}">
                <div class="mb-4">
                    <label class="block text-sm font-semibold text-gray-700 mb-1.5">Campo de coincidencia</label>
                    <select id="cc_match_field" class="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring focus:ring-blue-200 focus:border-blue-500">
                        <option value="codprod">Código de producto</option>
                        <option value="barra">Código de barras</option>
                    </select>
                </div>

                <div class="mb-4">
                    <label class="block text-sm font-semibold text-gray-700 mb-1.5">Moneda del archivo</label>
                    <select id="cc_moneda" class="w-full border border-gray-300 rounded-lg p-2.5 text-sm bg-white focus:ring focus:ring-blue-200 focus:border-blue-500">
                        <option value="BSS">Bolívares (BSS)</option>
                        <option value="USD">Dólares (USD)</option>
                    </select>
                </div>
            </div>

            <div class="mb-4 flex items-center justify-between">
                <label class="block text-sm font-semibold text-gray-700">Archivo (xlsx o xls)</label>
                <button type="button" id="cc_download_template" class="text-xs text-blue-600 hover:text-blue-800 font-medium underline">
                    Descargar plantilla
                </button>
            </div>
            <div>
                <input id="cc_file" type="file" accept=".xlsx,.xls"
                    class="w-full text-sm border border-gray-300 rounded-lg file:mr-3 file:py-2 file:px-3 file:border-0 file:rounded-l-lg file:bg-gray-100 file:text-gray-700 file:text-sm hover:file:bg-gray-200" />
            </div>
        </div>
    `;

    Swal.fire({
        title: `Cargar lista de precios / cantidades`,
        html: `<p class="text-base font-normal text-gray-500 -mt-2 mb-4">${marca}</p>${html}`,
        width: 480,
        showCancelButton: true,
        confirmButtonText: "Cargar",
        cancelButtonText: "Cancelar",
        reverseButtons: true,
        focusConfirm: false,
        customClass: {
            confirmButton: "custom-confirm-button",
        },
        didOpen: () => {
            const templateSelect = document.getElementById("cc_template") as HTMLSelectElement | null;
            const manualFields = document.getElementById("cc_manual_fields") as HTMLDivElement | null;

            if (templateSelect && manualFields) {
                const toggle = () => {
                    manualFields.classList.toggle("hidden", !!templateSelect.value);
                };
                templateSelect.addEventListener("change", toggle);
                toggle();
            }

            const costCheckboxes: [string, string][] = [
                ["cc_update_odc", "cc_odc_label"],
                ["cc_update_cendis", "cc_cendis_label"],
                ["cc_update_cantidad", "cc_cantidad_label"],
            ];

            costCheckboxes.forEach(([checkboxId, labelId]) => {
                const checkbox = document.getElementById(checkboxId) as HTMLInputElement | null;
                const label = document.getElementById(labelId) as HTMLLabelElement | null;
                if (!checkbox || !label) return;

                const sync = () => {
                    label.classList.toggle("border-blue-500", checkbox.checked);
                    label.classList.toggle("bg-blue-50", checkbox.checked);
                    label.classList.toggle("border-gray-300", !checkbox.checked);
                };
                checkbox.addEventListener("change", sync);
                sync();
            });

            const downloadBtn = document.getElementById("cc_download_template") as HTMLButtonElement | null;
            downloadBtn?.addEventListener("click", async () => {
                const matchField = (document.getElementById("cc_match_field") as HTMLSelectElement)?.value ?? "codprod";
                const updateOdc = (document.getElementById("cc_update_odc") as HTMLInputElement)?.checked ?? false;
                const updateCendis = (document.getElementById("cc_update_cendis") as HTMLInputElement)?.checked ?? false;
                const updateCantidad = (document.getElementById("cc_update_cantidad") as HTMLInputElement)?.checked ?? false;
                // Si no hay ninguno marcado, la plantilla muestra todas las columnas como referencia
                const noneChecked = !updateOdc && !updateCendis && !updateCantidad;
                try {
                    await downloadCostoCendisTemplate(
                        axios,
                        apiRoute,
                        matchField,
                        updateOdc || noneChecked,
                        updateCendis || noneChecked,
                        updateCantidad || noneChecked
                    );
                } catch (error) {
                    Swal.fire({ icon: "error", title: "No se pudo descargar la plantilla" });
                }
            });
        },
        preConfirm: async () => {
            const templateSelect = document.getElementById("cc_template") as HTMLSelectElement | null;
            const templateId = templateSelect?.value || null;
            const matchField = (document.getElementById("cc_match_field") as HTMLSelectElement)?.value ?? null;
            const moneda = (document.getElementById("cc_moneda") as HTMLSelectElement)?.value ?? null;
            const updateOdc = (document.getElementById("cc_update_odc") as HTMLInputElement)?.checked ?? false;
            const updateCendis = (document.getElementById("cc_update_cendis") as HTMLInputElement)?.checked ?? false;
            const updateCantidad = (document.getElementById("cc_update_cantidad") as HTMLInputElement)?.checked ?? false;
            const fileInput = document.getElementById("cc_file") as HTMLInputElement;
            const file = fileInput?.files?.[0];

            if (!updateOdc && !updateCendis && !updateCantidad) {
                Swal.showValidationMessage("Debes marcar al menos un campo a actualizar (ODC, Cendis y/o Cantidad).");
                return false;
            }

            if (!file) {
                Swal.showValidationMessage("Debes seleccionar un archivo.");
                return false;
            }

            try {
                const response = await uploadCostoCendis(
                    axios,
                    apiRoute,
                    pedidoId,
                    marca,
                    file,
                    templateId,
                    matchField,
                    moneda,
                    updateOdc,
                    updateCendis,
                    updateCantidad
                );
                return response.data;
            } catch (error: any) {
                const msg = error?.response?.data?.message ?? "No se pudo cargar el archivo.";
                Swal.showValidationMessage(msg);
                return false;
            }
        },
    }).then((result) => {
        if (!result.isConfirmed || !result.value) {
            return;
        }

        const resumen = result.value;
        const noEncontrados = resumen.no_encontrados_count ?? 0;

        Swal.fire({
            icon: noEncontrados > 0 ? "warning" : "success",
            title: "Carga completada",
            html: `
                <div class="text-left text-sm">
                    <div class="flex gap-3 mb-2">
                        <div class="flex-1 bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-center">
                            <div class="text-2xl font-semibold text-green-600">${resumen.actualizados ?? 0}</div>
                            <div class="text-xs text-green-700">Actualizados</div>
                        </div>
                        <div class="flex-1 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-center">
                            <div class="text-2xl font-semibold text-amber-600">${noEncontrados}</div>
                            <div class="text-xs text-amber-700">No encontrados</div>
                        </div>
                    </div>
                    ${noEncontrados > 0
                        ? `<p class="text-xs text-gray-500 mt-2">${(resumen.no_encontrados || []).join(", ")}</p>`
                        : ""
                    }
                </div>
            `,
        }).then(() => {
            if (onSuccess) {
                onSuccess();
            } else {
                store.dispatch('orders/fetchCartItems', { apiRoute, branch: marca });
            }
        });
    });
}
